import {AbstractControl, ValidationErrors, ValidatorFn} from "@angular/forms";

export const NORTH_AMERICA_DIAL_CODE = '+1';

// The USA and Canada share the +1 dial code, so the country of such a number is determined by its area code
export const CANADIAN_AREA_CODES: ReadonlySet<string> = new Set([
  '204', '226', '236', '249', '250', '257', '263', '289', '306', '343', '354', '365', '367', '368', '382',
  '403', '416', '418', '428', '431', '437', '438', '450', '468', '474', '506', '514', '519', '548', '579',
  '581', '584', '587', '604', '613', '639', '647', '672', '683', '705', '709', '742', '778', '780', '782',
  '807', '819', '825', '867', '873', '879', '902', '905'
]);

export type NorthAmericaCountry = 'US' | 'CA';

const COUNTRY_NAMES: Record<NorthAmericaCountry, string> = {US: 'United States', CA: 'Canada'};

export function isNorthAmericaCountry(countryIso: string | null | undefined): countryIso is NorthAmericaCountry {
  return countryIso === 'US' || countryIso === 'CA';
}

/** The country of a +1 number by its area code, or null while the area code is not typed in full */
export function getNorthAmericaCountryByPhone(phone: string | null | undefined): NorthAmericaCountry | null {
  const digits = (phone ?? '').replace(/\D/g, '');
  if (digits.length < 3) return null;

  return CANADIAN_AREA_CODES.has(digits.slice(0, 3)) ? 'CA' : 'US';
}

/**
 * Validates a +1 number (the USA or Canada): 10 digits, an area code that cannot start with 0 or 1 and that belongs
 * to the selected country. The country is passed as a getter because it is not part of the form value.
 * Has to go after the other phone validators because their errors share the same key.
 */
export function northAmericaPhoneValidator(getCountryIso: () => string | null | undefined): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const countryIso = getCountryIso();
    const digits = (control.value ?? '').replace(/\D/g, '');
    if (!isNorthAmericaCountry(countryIso) || !digits) return null;

    if (digits.length !== 10) return {error: 'Phone number must be 10 digits long.'};
    if (/^[01]/.test(digits)) return {error: 'Area code cannot start with 0 or 1.'};

    const phoneCountry = getNorthAmericaCountryByPhone(digits);
    if (phoneCountry !== countryIso) return {error: `This area code does not belong to ${COUNTRY_NAMES[countryIso]}.`};

    return null;
  };
}
